import { Bell, Plus } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link as HammerLink } from '@liveauctioneers/hammer-ui-core/link';
import { OptionType } from '@liveauctioneers/hammer-ui-core/select';
import { useMemo } from 'react';

import { SearchFilter } from '@/components/Pagination/hammer-pagination/SearchFilter';
import SortPageSizePair from '@/components/Pagination/hammer-pagination/SortPageSizePair';
import { savedSearchItemSortOrders } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { getTotalSavedSearchItems } from '@/redux/modules/savedSearch';
import { PaginationFilter } from '@/types/PaginationFilter';
import { getNotificationsUrl, getSearchBaseUrl } from '@/utils/urls';

type Props = {
    filters: PaginationFilter;
    onFilterChange: (filters: PaginationFilter) => void;
};

const SavedSearchFilter = ({ filters, onFilterChange }: Props) => {
    const manageUrl = getNotificationsUrl('searches');
    const newTermUrl = `${getSearchBaseUrl()}?newTerm=true`;
    const { formatMessage } = useIntl();
    const itemTab = filters.tab === 'items';
    const totalSavedSearchItems = useAppSelector(getTotalSavedSearchItems);

    const options: OptionType[] = useMemo(
        () => [
            {
                label: formatMessage({ id: 'search_alerts.sort.newlyListed' }),
                value: savedSearchItemSortOrders.NEWLY_LISTED,
            },
            {
                label: formatMessage({ id: 'search_alerts.sort.startingSoonest' }),
                value: savedSearchItemSortOrders.STARTING_SOONEST,
            },
        ],
        [formatMessage]
    );

    return (
        <div className="flex items-end justify-between gap-sm border-b border-border-secondary py-lg">
            <div className="flex gap-sm">
                {itemTab ? (
                    <SearchFilter
                        className="max-w-[300px] smMax:w-[50vw]"
                        onChange={onFilterChange}
                        paginationFilter={filters}
                        searchLabel={<FormattedMessage id="search_alerts.searchLabel" />}
                        searchPlaceholder={formatMessage(
                            {
                                id: 'search_alerts.resultsPlaceholder',
                            },
                            { count: totalSavedSearchItems }
                        )}
                        shouldModifyQueryParams
                    />
                ) : null}
                <SortPageSizePair
                    className="smMax:hidden"
                    hideSort={!itemTab}
                    onChange={onFilterChange}
                    pageSizeLabelClassName="pageSizeLabelCN"
                    paginationFilter={filters}
                    shouldModifyQueryParams
                    sortLabelClassName="mdMax:min-w-[150px]"
                    sortOptions={options}
                />
            </div>
            <div className="flex items-center gap-sm xsMax:hidden">
                <HammerLink
                    aria-label="Add New Search Term"
                    href={newTermUrl}
                >
                    <InlineButton
                        icon={Plus}
                        style="neutral"
                    >
                        <FormattedMessage id="search_alerts.add_new_search" />
                    </InlineButton>
                </HammerLink>
                <HammerLink
                    aria-label="Add New Search Term"
                    href={manageUrl}
                >
                    <InlineButton
                        icon={Bell}
                        style="neutral"
                    >
                        <FormattedMessage id="manageNotifications" />
                    </InlineButton>
                </HammerLink>
            </div>
        </div>
    );
};

export default SavedSearchFilter;
